// client/src/world/flora/species.ts
import type { SpeciesId, SpeciesParams } from "./types";

/** Rich foliage pigments; keep species distinct under bright daylight. */
export const SPECIES: Record<SpeciesId, SpeciesParams> = {
  // Finite inland freshwater basins select this family; never forestMix.
  cypress: {
    id: "cypress",
    trunk: { height: [13, 17], radius: .90, lean: .06, forks: [4, 4], forkHeight: .56 },
    canopy: { style: "cypress", count: [12, 12], size: [2.15, 2.75], rough: .24 },
    motifs: {},
    palette: { bark: [.17, .105, .065], leafSummer: [.22, .265, .065], leafAutumn: [.27, .25, .08] },
    deciduous: false,
  },
  // Dry grassland habitat selects these sparse stands; never forestMix.
  acacia: {
    id: "acacia",
    trunk: { height: [7.6, 9.6], radius: .57, lean: .10, forks: [3, 3], forkHeight: .46 },
    canopy: { style: "umbrella", count: [4, 4], size: [2.6, 3.2], rough: .24 },
    branching: { spread: .56, rise: .34, leafScale: 1 },
    motifs: {},
    palette: { bark: [.31, .23, .145], leafSummer: [.26, .29, .075], leafAutumn: [.26, .29, .075] },
    deciduous: false,
  },
  // Selected only by warm, dry coastal habitats; never the inland forest mix.
  palm: {
    id: "palm",
    trunk: { height: [10.5, 14], radius: .39, lean: .19, forks: [0, 0], forkHeight: 1 },
    canopy: { style: "pinnate", count: [12, 14], size: [3.7, 4.8], rough: .25 },
    motifs: { barkBands: true },
    palette: { bark: [.35, .27, .16], leafSummer: [.19, .29, .055], leafAutumn: [.19, .29, .055] },
    deciduous: false,
  },
  // Selected only by the sheltered, shallow-water habitat; never forestMix.
  mangrove: {
    id: "mangrove",
    trunk: { height: [8, 11], radius: .48, lean: .07, forks: [3, 4], forkHeight: .55 },
    canopy: { style: "blobs", count: [4, 6], size: [1.8, 2.5], rough: .30 },
    branching: { spread: .38, rise: .30, leafScale: .85 },
    propRoots: { count: 7, spread: .31, attach: .28 },
    motifs: {},
    palette: { bark: [.26, .16, .075], leafSummer: [.18, .29, .065], leafAutumn: [.18, .29, .065] },
    deciduous: false,
  },
  birch: {
    id: "birch",
    trunk: { height: [7, 10.5], radius: 0.24, lean: 0.03, forks: [2, 3], forkHeight: 0.62 },
    canopy: { style: "blobs", count: [3, 4], size: [0.85, 1.25], rough: 0.24 },
    branching: { spread: .22, rise: .38, leafScale: .65 },
    motifs: { barkBands: true },
    palette: { bark: [0.88, 0.87, 0.82], leafSummer: [0.24, 0.46, 0.08], leafAutumn: [0.88, 0.66, 0.18] },
    deciduous: true,
  },
  pine: {
    id: "pine",
    // Tall forest pines retain their collision dimensions. The conifer profile
    // controls the lower foliage start and the dense branch-spray envelope.
    trunk: { height: [12, 18], radius: 0.38, lean: 0.02, forks: [0, 0], forkHeight: 0.42 },
    canopy: { style: "tiers", count: [6, 7], size: [1.3, 1.8], rough: 0 },
    conifer: { droop: .14, spread: 1.08, fullness: .43, crownBase: .22 },
    motifs: {},
    palette: { bark: [0.36, 0.26, 0.18], leafSummer: [0.075, 0.24, 0.1], leafAutumn: [0.22, 0.38, 0.19] },
    deciduous: false,
  },
  spruce: {
    id: "spruce",
    trunk: { height: [8.5, 13], radius: 0.32, lean: 0.015, forks: [0, 0], forkHeight: 0.16 },
    canopy: { style: "droopTiers", count: [6, 7], size: [1.2, 1.7], rough: 0 },
    conifer: { droop: .24, spread: 1, fullness: .55, crownBase: .18 },
    motifs: {},
    palette: { bark: [0.3, 0.22, 0.16], leafSummer: [0.045, 0.18, 0.075], leafAutumn: [0.14, 0.3, 0.17] },
    deciduous: false,
  },
  oak: {
    id: "oak",
    trunk: { height: [4.5, 6.5], radius: 0.46, lean: 0.05, forks: [3, 4], forkHeight: 0.58 },
    canopy: { style: "blobs", count: [4, 6], size: [1.1, 1.55], rough: 0.22 },
    branching: { spread: .46, rise: .32, leafScale: .68 },
    motifs: {},
    palette: { bark: [0.34, 0.24, 0.16], leafSummer: [0.12, 0.30, 0.055], leafAutumn: [0.82, 0.45, 0.12] },
    deciduous: true,
  },
  willow: {
    id: "willow",
    trunk: { height: [4.5, 7], radius: 0.4, lean: 0.06, forks: [3, 5], forkHeight: 0.6 },
    canopy: { style: "fronds", count: [7, 10], size: [0.65, 0.95], rough: 0.18 },
    motifs: {},
    palette: { bark: [0.32, 0.26, 0.18], leafSummer: [0.26, 0.43, 0.09], leafAutumn: [0.75, 0.62, 0.24] },
    deciduous: true,
  },
  sakura: {
    id: "sakura",
    trunk: { height: [4.5, 6.5], radius: 0.38, lean: 0.07, forks: [3, 4], forkHeight: 0.55 },
    canopy: { style: "blobs", count: [4, 5], size: [0.95, 1.35], rough: 0.2 },
    branching: { spread: .48, rise: .25, leafScale: .68 },
    motifs: { blossoms: true },
    palette: { bark: [0.3, 0.2, 0.16], leafSummer: [0.9, 0.55, 0.65], leafAutumn: [0.95, 0.5, 0.4], blossom: [0.98, 0.8, 0.86] },
    deciduous: true,
  },
  forest: {
    id: "forest",
    trunk: { height: [6, 9.5], radius: 0.38, lean: 0.04, forks: [2, 4], forkHeight: 0.56 },
    canopy: { style: "blobs", count: [4, 6], size: [1.1, 1.5], rough: 0.22 },
    branching: { spread: .31, rise: .37, leafScale: .66 },
    motifs: {},
    palette: { bark: [0.33, 0.24, 0.17], leafSummer: [0.10, 0.28, 0.07], leafAutumn: [0.8, 0.5, 0.15] },
    deciduous: true,
  },
};
